import type { FriendRequestEntry, PartyMemberView, PartyResult, PartyView, ServerMessage } from "@golazo/shared";
import type { OnlineClient } from "./client.js";

/**
 * Tu grupo, en vivo (la escuadra de Fortnite, el lobby de CS2).
 *
 * El servidor manda el grupo entero cada vez que cambia; aqui se guarda el
 * ultimo, se calcula que ha cambiado (quien ha entrado, quien se ha ido, quien
 * manda ahora) para poder avisarlo, y se guardan las invitaciones a grupos de
 * otros que siguen vivas.
 */
export interface PartyInvite {
  partyId: string;
  from: FriendRequestEntry;
  /** Cuantos habia dentro al invitarte. */
  size: number;
  at: number;
}

export type PartyEvent =
  | { kind: "invited"; invite: PartyInvite }
  | { kind: "requested"; from: FriendRequestEntry }
  | { kind: "result"; clientId: string; result: PartyResult }
  /** El lider ha entrado en una sala: toca ir. */
  | { kind: "follow"; roomId: string; from: FriendRequestEntry }
  | { kind: "joined"; who: PartyMemberView }
  | { kind: "left"; who: PartyMemberView }
  | { kind: "leader"; who: PartyMemberView }
  /** Ya no estas en ningun grupo: te has ido, te han echado o te has quedado solo. */
  | { kind: "gone"; reason: "left" | "kicked" | "empty" };

export interface PartyStore {
  state(): PartyView | null;
  subscribe(fn: (party: PartyView | null) => void): () => void;
  onEvent(fn: (event: PartyEvent) => void): () => void;
  /** Invitaciones a grupos de otros, vivas, la mas nueva primero. */
  invites(): PartyInvite[];
  /** Estas en un grupo de verdad (con alguien mas). */
  inParty(): boolean;
  isLeader(): boolean;
  /** Los demas de tu grupo. */
  mates(): PartyMemberView[];
  isMate(clientId: string): boolean;
  invite(clientId: string): void;
  join(partyId: string): void;
  request(partyId: string): void;
  answer(clientId: string, accept: boolean): void;
  decline(partyId: string): void;
  leave(): void;
  kick(clientId: string): void;
  promote(clientId: string): void;
  setOpen(open: boolean): void;
  dispose(): void;
}

/** Lo que dura una invitacion a grupo: lo mismo que en el servidor. */
const INVITE_TTL_MS = 5 * 60_000;

export function createPartyStore(client: OnlineClient): PartyStore {
  let current: PartyView | null = null;
  const listeners = new Set<(party: PartyView | null) => void>();
  const eventListeners = new Set<(event: PartyEvent) => void>();
  const pending = new Map<string, PartyInvite>();
  const emit = (event: PartyEvent): void => {
    for (const fn of eventListeners) fn(event);
  };
  const notify = (): void => {
    for (const fn of listeners) fn(current);
  };
  const liveInvites = (): PartyInvite[] => {
    const now = Date.now();
    for (const [id, inv] of pending) if (now - inv.at > INVITE_TTL_MS) pending.delete(id);
    return [...pending.values()].sort((a, b) => b.at - a.at);
  };

  const off = client.onMessage((msg: ServerMessage) => {
    if (msg.type === "party:sync") {
      const before = current;
      current = msg.party;
      const me = client.clientId;
      if (before && current && before.id === current.id) {
        const was = new Set(before.members.map((m) => m.clientId));
        const now = new Set(current.members.map((m) => m.clientId));
        for (const m of current.members) if (!was.has(m.clientId) && m.clientId !== me) emit({ kind: "joined", who: m });
        for (const m of before.members) if (!now.has(m.clientId) && m.clientId !== me) emit({ kind: "left", who: m });
        if (before.leaderId !== current.leaderId) {
          const who = current.members.find((m) => m.clientId === current!.leaderId);
          if (who) emit({ kind: "leader", who });
        }
      }
      if (current) pending.delete(current.id);
      if (!current && before && msg.reason) emit({ kind: "gone", reason: msg.reason });
      notify();
    } else if (msg.type === "party:invited") {
      const invite: PartyInvite = { partyId: msg.partyId, from: msg.from, size: msg.size, at: Date.now() };
      pending.set(invite.partyId, invite);
      emit({ kind: "invited", invite });
      notify();
    } else if (msg.type === "party:requested") {
      emit({ kind: "requested", from: msg.from });
    } else if (msg.type === "party:result") {
      emit({ kind: "result", clientId: msg.clientId, result: msg.result });
    } else if (msg.type === "party:follow") {
      emit({ kind: "follow", roomId: msg.roomId, from: msg.from });
    }
  });

  const inParty = (): boolean => (current?.members.length ?? 0) >= 2;

  return {
    state: () => current,
    subscribe(fn) {
      listeners.add(fn);
      fn(current);
      return () => listeners.delete(fn);
    },
    onEvent(fn) {
      eventListeners.add(fn);
      return () => eventListeners.delete(fn);
    },
    invites: liveInvites,
    inParty,
    isLeader: () => current !== null && current.leaderId === client.clientId,
    mates: () => current?.members.filter((m) => m.clientId !== client.clientId) ?? [],
    isMate: (clientId) => current?.members.some((m) => m.clientId === clientId && clientId !== client.clientId) ?? false,
    invite: (clientId) => client.send({ type: "party:invite", clientId }),
    join(partyId) {
      pending.delete(partyId);
      client.send({ type: "party:join", partyId });
      notify();
    },
    request: (partyId) => client.send({ type: "party:request", partyId }),
    answer: (clientId, accept) => client.send({ type: "party:answer", clientId, accept }),
    decline(partyId) {
      if (!pending.delete(partyId)) return;
      client.send({ type: "party:decline", partyId });
      notify();
    },
    leave: () => client.send({ type: "party:leave" }),
    kick: (clientId) => client.send({ type: "party:kick", clientId }),
    promote: (clientId) => client.send({ type: "party:promote", clientId }),
    setOpen: (open) => client.send({ type: "party:open", open }),
    dispose() {
      off();
      listeners.clear();
      eventListeners.clear();
    },
  };
}
